import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminData } from '../_models/admin';
import { ApiService } from '../_services/api.service';
import { ChecksService } from '../_services/checks.service';
import { NotificationService } from '../_services/notification.service';

interface FinancePlSection {
  slug: string;
  title: string;
  summary: string;
}

@Component({
  selector: 'app-finance-pl',
  templateUrl: './finance-pl.component.html',
  styleUrl: './finance-pl.component.css'
})
export class FinancePlComponent implements OnInit, OnDestroy {
  sections: FinancePlSection[] = [
    { slug: 'pl-statement', title: 'P&L statement', summary: 'Income versus expense view for CFO, COO, and CEO.' },
    { slug: 'manual-expense-input', title: 'Manual expense input', summary: 'Capture manual expenses and submit them for approval.' },
    { slug: 'trade-income-recognition', title: 'Trade income recognition', summary: 'Recognize income against completed Treasury trade records.' },
    { slug: 'income-expense-exports', title: 'Income/expense exports', summary: 'Export filtered income and expense data.' },
  ];

  activeSection: FinancePlSection = this.sections[0];
  private routeSubscription: Subscription;
  currentUser: AdminData;
  canViewPl = false;
  receiptFile: File = null;

  expenses: any[] = [];
  incomes: any[] = [];
  trades: any[] = [];
  categories: string[] = [];
  pl: any = null;

  periodForm = this.fb.group({ period: ['monthly'], date: [this.today()] });
  expenseForm = this.fb.group({ category: ['', Validators.required], amount: [0, Validators.required], expenseDate: [this.today(), Validators.required], description: ['', Validators.required] });
  incomeForm = this.fb.group({ tradeRef: ['', Validators.required], category: ['Trade income', Validators.required], amount: [0, Validators.required], incomeDate: [this.today(), Validators.required], notes: [''] });
  exportForm = this.fb.group({ dateFrom: [this.monthStart()], dateTo: [this.today()], category: [''], format: ['Excel'] });

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private apiService: ApiService,
    public checkService: ChecksService,
    private notifyService: NotificationService,
  ) {
    this.checkService.checkSession();
  }

  ngOnInit(): void {
    this.currentUser = this.checkService.getAdminData();
    this.routeSubscription = this.route.paramMap.subscribe(params => {
      const sectionSlug = params.get('section') || this.sections[0].slug;
      this.activeSection = this.sections.find(section => section.slug === sectionSlug) || this.sections[0];
      this.loadFinanceData();
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  loadFinanceData(): void {
    this.apiService.getFinancePlData({ ...this.exportForm.value, ...this.periodForm.value }).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        const payload = data.data || {};
        this.expenses = payload.expenses || [];
        this.incomes = payload.incomes || [];
        this.trades = payload.trades || [];
        this.categories = payload.categories || [];
        this.canViewPl = payload.canViewPl === true;
        this.pl = payload.pl;
      }
    });
  }

  refreshPl(): void {
    this.apiService.getFinancePlStatement(this.periodForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.canViewPl = true;
        this.pl = data.data;
      } else {
        this.canViewPl = false;
      }
    });
  }

  submitExpense(): void {
    if (this.expenseForm.invalid || Number(this.expenseForm.value.amount || 0) <= 0) {
      this.expenseForm.markAllAsTouched();
      return;
    }
    this.apiService.createFinanceExpense(this.expenseForm.value, this.receiptFile).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Expense submitted for approval', 'Finance & P&L');
        this.expenseForm.patchValue({ category: '', amount: 0, description: '' });
        this.receiptFile = null;
        this.loadFinanceData();
      }
    });
  }

  receiptSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.receiptFile = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  recognizeIncome(): void {
    if (this.incomeForm.invalid || Number(this.incomeForm.value.amount || 0) <= 0) {
      this.incomeForm.markAllAsTouched();
      return;
    }
    this.apiService.recognizeFinanceTradeIncome(this.incomeForm.value).subscribe(data => {
      this.checkService.checkLoggedin(data);
      if (data.success === true) {
        this.notifyService.showSuccess('Trade income recognized', 'Finance & P&L');
        this.incomeForm.patchValue({ tradeRef: '', amount: 0, notes: '' });
        this.loadFinanceData();
      }
    });
  }

  tradeLabel(trade: any): string {
    return `${trade.tradeId || trade.ref} · ${trade.pair} · ${Number(trade.amount || 0).toLocaleString()}`;
  }

  maxTrend(): number {
    const trend = this.pl?.trend || [];
    return Math.max(1, ...trend.map(row => Math.max(Number(row.income || 0), Number(row.expense || 0))));
  }

  barWidth(value: number): string {
    return `${Math.max(3, (Number(value || 0) / this.maxTrend()) * 100)}%`;
  }

  filteredExpenses(): any[] {
    const filters = this.exportForm.value;
    return this.expenses.filter(row => this.inRange(row.expenseDate, row.category, filters));
  }

  filteredIncomes(): any[] {
    const filters = this.exportForm.value;
    return this.incomes.filter(row => this.inRange(row.incomeDate, row.category, filters));
  }

  inRange(date: string, category: string, filters: any): boolean {
    return (!filters.dateFrom || date >= filters.dateFrom)
      && (!filters.dateTo || date <= filters.dateTo)
      && (!filters.category || category === filters.category);
  }

  exportFinanceData(format: string = 'Excel'): void {
    const rows = [
      ['Type', 'Date', 'Category', 'Amount', 'Description/Notes'],
      ...this.filteredIncomes().map(row => ['Income', row.incomeDate, row.category, row.amount, row.notes || '']),
      ...this.filteredExpenses().map(row => ['Expense', row.expenseDate, row.category, row.amount, row.description || '']),
    ];
    const html = `<html><body><h1>Finance Income and Expense Export</h1><table border="1">${rows.map(row => '<tr>'+row.map(cell => '<td>'+cell+'</td>').join('')+'</tr>').join('')}</table></body></html>`;
    if (format === 'PDF') {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(html);
        win.document.close();
        win.print();
      }
      return;
    }
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'finance-income-expense-export.xls';
    link.click();
    window.URL.revokeObjectURL(url);
  }

  today(): string {
    return new Date().toISOString().slice(0, 10);
  }

  monthStart(): string {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().slice(0, 10);
  }
}
